import { el } from '../lib/dom.js';
import { listTimezones, browserTimezone, timezoneLabel } from '../lib/timezone.js';

// ---------------------------------------------------------------------------
// Access-window form pieces shared by org-new.js (creation) and org-detail.js
// (the Trial window / CMAI Core window update forms):
//
//   - accessTimezoneSelect: the REQUIRED timezone every window's dates are
//     entered in. A start date means 00:00 and an end date 23:59 in it (see
//     api/src/core/trial-schedule.ts). Pre-selected from the data region.
//   - trialScheduleControl: opt-in exact trial start/end times (in that same
//     timezone), collapsed by default.
// ---------------------------------------------------------------------------

/** Default access timezone per data region (OrgConfig.dataResidency). */
export const REGION_TIMEZONE = { AU: 'Australia/Sydney', US: 'America/New_York' };

/** The timezone to pre-select: the org's saved one, else its data region's, else the browser's. */
export function defaultAccessTimezone(savedTimezone, dataResidency) {
  return savedTimezone || REGION_TIMEZONE[dataResidency] || browserTimezone();
}

/** Required IANA timezone `<select>` for an access window. */
export function accessTimezoneSelect({ id, value, onchange } = {}) {
  const zones = listTimezones();
  if (value && !zones.includes(value)) zones.unshift(value);
  return el(
    'select',
    { class: 'input', id, required: true, onchange },
    zones.map((tz) => el('option', { value: tz, selected: tz === value }, timezoneLabel(tz))),
  );
}

let uid = 0;

/**
 * @returns {{
 *   root: HTMLElement,
 *   reset: () => void,
 *   setValue: (v: { trialStartTime?: string, trialEndTime?: string }) => void,
 *   getValue: () => { trialStartTime?: string, trialEndTime?: string },
 * }}
 */
export function trialScheduleControl() {
  const prefix = `trial-sched-${(uid += 1)}`;

  const toggle = el('input', { type: 'checkbox', id: `${prefix}-toggle` });
  const startTime = el('input', { class: 'input mono', type: 'time', id: `${prefix}-start-time` });
  const endTime = el('input', { class: 'input mono', type: 'time', id: `${prefix}-end-time` });

  const fields = el(
    'div',
    { class: 'field-grid schedule-fields', hidden: true },
    el('div', { class: 'field' }, el('label', { for: `${prefix}-start-time` }, 'Start time'), startTime),
    el('div', { class: 'field' }, el('label', { for: `${prefix}-end-time` }, 'End time'), endTime),
  );

  toggle.addEventListener('change', () => {
    fields.hidden = !toggle.checked;
  });

  const root = el(
    'div',
    { class: 'trial-schedule' },
    el(
      'label',
      { class: 'checkbox-field', for: `${prefix}-toggle` },
      toggle,
      el('span', { class: 'box' }),
      'Set exact trial start/end times (instead of 00:00 / 23:59)',
    ),
    fields,
  );

  return {
    root,
    reset() {
      toggle.checked = false;
      startTime.value = '';
      endTime.value = '';
      fields.hidden = true;
    },
    /** Pre-populate exact times an org already has, opening the section so the choice is visible rather than silently reverting on the next save. */
    setValue({ trialStartTime, trialEndTime }) {
      toggle.checked = true;
      startTime.value = trialStartTime ?? '';
      endTime.value = trialEndTime ?? '';
      fields.hidden = false;
    },
    getValue() {
      if (!toggle.checked) return {};
      return {
        trialStartTime: startTime.value || undefined,
        trialEndTime: endTime.value || undefined,
      };
    },
  };
}
